import { CityState, Building, Parcel, ZoneType } from './types';
import { BuildingCatalog } from './BuildingCatalog';

export class BuildingEngine {
  /**
   * Drives progressive construction stages and handles density level upgrades.
   */
  public tickGrowth(state: CityState) {
    const buildings = state.buildings;
    const parcels = state.parcels;

    // 1. Progress under-construction buildings
    for (const key of Object.keys(buildings)) {
      const b = buildings[key];
      if (b.stage !== 'completed' && b.stage !== 'damaged' && b.stage !== 'rubble') {
        // Enforce utility checking!
        const keyCell = `${b.x},${b.y}`;
        const isPowered = !!state.powerGrid[keyCell] || (b.zone === 'civic' && (b.familyId.includes('power') || b.familyId.includes('water')));
        const isWatered = !!state.waterGrid[keyCell] || (b.zone === 'civic' && (b.familyId.includes('power') || b.familyId.includes('water')));

        // If not powered or watered, stall the construction progress
        if (!isPowered || !isWatered) {
          continue;
        }

        b.progress += Math.floor(Math.random() * 15) + 12; // 12-27% progress daily

        if (b.progress >= 100) {
          b.progress = 0;
          if (b.stage === 'planned') {
            b.stage = 'foundation';
          } else if (b.stage === 'foundation') {
            b.stage = 'construction';
          } else if (b.stage === 'construction') {
            b.stage = 'completed';
            b.progress = 100;

            // Set capacity based on static catalog values!
            const catalogKey = `${b.familyId}_lvl${b.level}`;
            const entry = BuildingCatalog[catalogKey];
            b.capacity = entry ? entry.capacity : b.level * 4;
            b.occupancy = b.capacity; // auto-occupy

            // link with parent parcel
            const p = parcels[key];
            if (p) p.buildingId = b.id;
          }
        }
      } else {
        b.age += 1;
        
        // Enforce utility checking for occupied buildings
        const keyCell = `${b.x},${b.y}`;
        const isPowered = !!state.powerGrid[keyCell] || (b.zone === 'civic' && (b.familyId.includes('power') || b.familyId.includes('water')));
        const isWatered = !!state.waterGrid[keyCell] || (b.zone === 'civic' && (b.familyId.includes('power') || b.familyId.includes('water')));

        if (!isPowered || !isWatered) {
          b.occupancy = 0; // citizens abandon the building without power or water!
        } else {
          // Re-populate to full capacity when utilities are active
          const catalogKey = `${b.familyId}_lvl${b.level}`;
          const entry = BuildingCatalog[catalogKey];
          b.capacity = entry ? entry.capacity : b.level * 4;
          b.occupancy = b.capacity;
        }

        // 2. High-Fidelity upgrade triggers:
        // Completed low-level buildings redevelop if land value and demand are high, AND utilities are functional!
        if (b.level < 6 && b.age > 45 && Math.random() < 0.04 && isPowered && isWatered) {
          const p = parcels[key];
          if (p && p.landValue > 140 + b.level * 25) {
            this.redevelopToNextLevel(b);
          }
        }
      }
    }

    // 3. Spawns private zoned properties on empty frontage parcels
    this.trySpawnBuildings(state);
  }

  public spawnCivicBuilding(x: number, y: number, state: CityState, type: string): Building | null {
    const key = `${x},${y}`;
    if (state.buildings[key] || state.roads[key]) return null;

    let name = 'Edificio Civico';
    let familyId = 'civic';
    let zoneType: ZoneType = 'civic';
    
    if (type === 'park') {
      name = 'Parco Comunale';
      familyId = 'park';
      zoneType = 'park';
    } else if (type === 'power_solar') {
      name = 'Centrale Solare';
      familyId = 'power_solar';
      zoneType = 'civic';
    } else if (type === 'power_coal') {
      name = 'Centrale a Carbone';
      familyId = 'power_coal';
      zoneType = 'civic';
    } else if (type === 'water_tower') {
      name = 'Torre dell\'Acqua';
      familyId = 'water_tower';
      zoneType = 'civic';
    } else if (type === 'water_pump') {
      name = 'Stazione di Pompaggio';
      familyId = 'water_pump';
      zoneType = 'civic';
    } else if (type === 'police') {
      name = 'Stazione di Polizia';
      familyId = 'police';
      zoneType = 'civic';
    } else if (type === 'fire') {
      name = 'Caserma dei Pompieri';
      familyId = 'fire';
      zoneType = 'civic';
    } else if (type === 'hospital') {
      name = 'Ospedale Generale';
      familyId = 'hospital';
      zoneType = 'civic';
    } else if (type === 'school') {
      name = 'Scuola Elementare';
      familyId = 'school';
      zoneType = 'civic';
    } else if (type === 'stadium') {
      name = 'Grande Stadio';
      familyId = 'stadium';
      zoneType = 'civic';
    } else if (type === 'panoramic_tower') {
      name = 'Torre Panoramica';
      familyId = 'panoramic_tower';
      zoneType = 'civic';
    } else if (type === 'central_station') {
      name = 'Stazione Centrale';
      familyId = 'central_station';
      zoneType = 'civic';
    } else if (type === 'airport') {
      name = 'Aeroporto Internazionale';
      familyId = 'airport';
      zoneType = 'civic';
    } else if (type === 'port') {
      name = 'Porto Fluviale';
      familyId = 'port';
      zoneType = 'civic';
    }

    const id = `civic_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
    const b: Building = {
      id,
      x,
      y,
      zone: zoneType,
      density: 'medium',
      level: 1,
      stage: 'planned',
      progress: 0,
      occupancy: 0,
      capacity: 0,
      landValue: 200,
      name,
      age: 0,
      familyId,
      assetVariant: Math.floor(Math.random() * 5)
    };

    state.buildings[key] = b;

    // virtual parcel
    state.parcels[key] = {
      id: `parcel_${id}`,
      x,
      y,
      zone: zoneType,
      density: 'medium',
      landValue: 200,
      buildingId: id,
      frontageRoadKey: null
    };

    return b;
  }

  private redevelopToNextLevel(b: Building) {
    b.level += 1;
    b.stage = 'planned'; // put back under construction!
    b.progress = 0;
    b.occupancy = 0;
    b.capacity = 0;

    // Resolve name from upgraded catalog level
    const catalogKey = `${b.familyId}_lvl${b.level}`;
    const entry = BuildingCatalog[catalogKey];
    if (entry) {
      b.name = entry.name;
    }
  }

  private trySpawnBuildings(state: CityState) {
    const demand = state.demand;
    const parcels = state.parcels;

    const keys = Object.keys(parcels).sort(() => Math.random() - 0.5);

    for (const key of keys) {
      const p = parcels[key];
      if (state.buildings[key]) continue;

      if (p.zone === 'residential' && demand.residential > 0) {
        this.spawnBuildingAtParcel(p, 'residential', state);
        demand.residential -= 10;
      } else if (p.zone === 'commercial' && demand.commercial > 0) {
        this.spawnBuildingAtParcel(p, 'commercial', state);
        demand.commercial -= 10;
      } else if (p.zone === 'industrial' && demand.industrial > 0) {
        this.spawnBuildingAtParcel(p, 'industrial', state);
        demand.industrial -= 10;
      }
    }
  }

  private spawnBuildingAtParcel(p: Parcel, zone: ZoneType, state: CityState) {
    const key = `${p.x},${p.y}`;
    
    // Choose building family based on coordinate coordinates to cluster themes
    const families = {
      residential: ['res_suburban', 'res_modern', 'res_classic', 'res_green', 'res_highrise'],
      commercial: ['com_retail', 'com_food', 'com_leisure', 'com_mall', 'com_tower'],
      industrial: ['ind_workshop', 'ind_manufacture', 'ind_hitech', 'ind_logistics', 'ind_heavy']
    };

    const zoneFamilies = families[zone as keyof typeof families] || ['res_suburban'];
    const familyId = zoneFamilies[(p.x * 3 + p.y * 7) % zoneFamilies.length];

    // Spawns corresponding density tiers
    let level = 1;
    if (p.density === 'medium') level = 3;
    if (p.density === 'high') level = 5;

    const catalogKey = `${familyId}_lvl${level}`;
    const entry = BuildingCatalog[catalogKey];

    const b: Building = {
      id: `b_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      x: p.x,
      y: p.y,
      zone,
      density: p.density,
      level,
      stage: 'planned',
      progress: 0,
      occupancy: 0,
      capacity: 0,
      landValue: p.landValue,
      name: entry ? entry.name : 'Starter Structure',
      age: 0,
      familyId,
      assetVariant: Math.floor(Math.random() * 10)
    };

    state.buildings[key] = b;
    p.buildingId = b.id;
  }
}
